import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  COARSE_AGGREGATE_DISPLAY_PLACES,
  RESULT_TEMPLATE,
  T96_GRADING_FRACTIONS,
  templateEquipmentRoles,
  isD2216Document,
  isD2726Document,
  isT85Document,
  isT96Document,
  type D2041Document,
  type D2726Document,
  type T85Document,
  type RevisionAttachmentEntry,
  type RevisionComparison,
  type RevisionEquipmentEntry,
} from '@lims/shared-types';
import type {
  ResultHistoryView,
  ResultLockView,
  ResultRevisionView,
} from '../../api/endpoints.js';
import {
  Alert,
  Panel,
  ReadOnlyField,
  Reading,
  StatusBadge,
} from '../../components/ui/index.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { formatCalendarDate, recordedEquipmentName, toneForChangeKind } from './status.js';
import { D2216Record } from './D2216Record.js';
import { LosAngelesAbrasionRecord } from './LosAngelesAbrasionForm.js';
import { SpecificGravityRecord } from './SpecificGravityRecord.js';
import { specificGravityFormFor } from './SpecificGravityForm.js';
import { CheckList, SubSection, type CheckItem } from './bench-parts.js';
import { ResultBlock, ResultGrid } from './specialized-form/index.js';
import { formatDecimalForDisplay, MOISTURE_DISPLAY_PLACES } from '@lims/shared-types';

/**
 * THE SHARED PARTS OF TECHNICAL REVIEW AND FINAL APPROVAL — modules C7 and C8.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTIONS 4 AND 8 BEFORE ADDING ANYTHING TO THIS FILE.
 * ===========================================================================
 *
 * Four rules govern everything below, and each of them is a requirement rather
 * than a preference:
 *
 *  1. NOTHING HERE IS A VERDICT ON THE MATERIAL. A review says a RECORD was
 *     checked; an approval says a second person checked the same RECORD. There
 *     is no conformity wording, no pass, no fail, no limit, no chart and no
 *     colour standing in for one — for any test.
 *
 *  2. THE COMPARISON IS RENDERED, NEVER RE-DERIVED. The backend computes it
 *     with the shared `compareRevisions`, and this file draws exactly what it
 *     is given. A second implementation of the same diff could disagree with
 *     the recorded one, and then two people would read different accounts of
 *     the same change.
 *
 *  3. AN EMPTIED FIELD IS REMOVED, NOT EMPTY AND NOT ZERO. A field whose
 *     content went away is labelled `removed` and its new state is stated in
 *     words — never drawn as a blank cell, a dash or a 0.
 *
 *  4. NOTHING IS EVER HIDDEN. Every revision's full content, every review and
 *     approval action, every correction and every return reason stays on the
 *     page, verbatim, in the order it happened.
 */

/** T85 and T96 calculated values, shown to their workbooks' number formats. */
const CALCULATED_DISPLAY_PLACES: readonly (readonly [RegExp, number])[] = [
  [/^t85\.(row\.[^.]+\.absorptionPercent|averageAbsorptionPercent)$/, COARSE_AGGREGATE_DISPLAY_PLACES.absorption],
  [/^t85\.(row\.[^.]+\.\w+SpecificGravity\w*|average\w+SpecificGravity\w*)$/, COARSE_AGGREGATE_DISPLAY_PLACES.specificGravity],
  [/^t96\.(totalInitialMass|passingMassNo12)$/, COARSE_AGGREGATE_DISPLAY_PLACES.mass],
  [/^t96\.lossPercent$/, COARSE_AGGREGATE_DISPLAY_PLACES.abrasionLoss],
];

/** The T96 values the server calculates, named under `t96.calculated`. */
const T96_CALCULATED: Readonly<Record<string, true>> = Object.freeze({
  totalInitialMass: true,
  passingMassNo12: true,
  lossPercent: true,
});

/** The two general-result fields that hold an instant rather than prose. */
const TIMESTAMP_FIELDS = new Set(['executionStartedAt', 'executionCompletedAt', 'd2216.ovenStartedAt']);

/**
 * The label of one compared field. A D2216 determination is named by its
 * position and can number in the revisions being compared (rows are matched
 * by their stable id, never by position).
 */
function changeLabel(
  field: string,
  t: (key: string, options?: Record<string, unknown>) => string,
  rowNames: ReadonlyMap<string, string> | undefined,
): string {
  const row = /^d2216\.row\.([^.]+)\.(\w+)$/.exec(field);
  if (row) {
    const name = rowNames?.get(row[1]!) ?? t('d2216.determinationRemoved');
    const part = row[2] === 'waterContentPercent' ? t('d2216.waterContent') : t(`d2216.field.${row[2]}`);
    return `${name} · ${part}`;
  }
  if (field.startsWith('d2216.')) return t(`d2216.${field.slice('d2216.'.length)}`);
  // ASTM D2726 and D2041 (D265): rows by stable id, values by their own names.
  const gravityRow = /^(d2726|d2041)\.row\.([^.]+)\.(\w+)$/.exec(field);
  if (gravityRow) {
    const [, ns, id, key] = gravityRow;
    const name = rowNames?.get(id!) ?? t('d2216.determinationRemoved');
    const part = /SpecificGravity$/.test(key!) ? t(`${ns}.calculated.${key}`) : t(`${ns}.field.${key}`);
    return `${name} · ${part}`;
  }
  const gravityField = /^(d2726|d2041)\.(\w+)$/.exec(field);
  if (gravityField) {
    const [, ns, key] = gravityField;
    if (/^average/.test(key!)) return t(`${ns}.average`);
    if (key === 'ambientTemperature' || key === 'relativeHumidity' || key === 'remarks') return t(`d2216.${key}`);
    return t(`${ns}.${key}`);
  }
  // AASHTO T85 and T96 (D274): T85 rows by stable id; T96 fractions by key.
  const t85Row = /^t85\.row\.([^.]+)\.(\w+)$/.exec(field);
  if (t85Row) {
    const [, id, key] = t85Row;
    const name = rowNames?.get(id!) ?? t('d2216.determinationRemoved');
    const part = /^(absorption|bulk|apparent)/.test(key!) ? t(`t85.calculated.${key}`) : t(`t85.field.${key}`);
    return `${name} · ${part}`;
  }
  if (field === 't85.remarks' || field === 't96.remarks') return t('d2216.remarks');
  if (field.startsWith('t85.average')) return t(`t85.average.${field.slice('t85.'.length)}`);
  const t96Fraction = /^t96\.grading\.(\w+)$/.exec(field);
  if (t96Fraction) {
    const sizes = T96_GRADING_FRACTIONS.find((fraction) => fraction.key === t96Fraction[1]);
    return sizes ? t('t96.fraction', { passing: sizes.passing, retained: sizes.retained }) : field;
  }
  const t96Field = /^t96\.(\w+)$/.exec(field);
  if (t96Field) {
    const key = t96Field[1]!;
    return key in T96_CALCULATED ? t(`t96.calculated.${key}`) : t(`t96.${key}`);
  }
  return t(`resultField.${field}`);
}

/** The direction and language a general-result field is written in. */
function fieldTextProps(field: string): { dir?: 'rtl' | 'ltr' | 'auto'; lang?: string } {
  if (field === 'summaryAr') return { dir: 'rtl', lang: 'ar' };
  if (field === 'summaryEn') return { dir: 'ltr', lang: 'en' };
  return { dir: 'auto' };
}

/**
 * One recorded value.
 *
 * `absentLabel` is what an ABSENT value reads as, and the caller chooses it:
 * "not recorded" where nothing was ever written, "removed" where somebody
 * emptied a field that had content. The distinction is the whole point — a
 * blank cell would collapse the two.
 */
function FieldValue({
  field,
  value,
  absentLabel,
}: {
  readonly field: string;
  readonly value: string | null;
  readonly absentLabel: string;
}) {
  const locale = currentLocale();

  if (value === null) {
    return <span className="text-caption font-medium text-default-600">{absentLabel}</span>;
  }
  if (field.endsWith('.waterContentPercent')) {
    return (
      <span dir="ltr">
        {formatDecimalForDisplay(value, MOISTURE_DISPLAY_PLACES.waterContent) ?? value}
      </span>
    );
  }
  const shaped = CALCULATED_DISPLAY_PLACES.find(([pattern]) => pattern.test(field));
  if (shaped) {
    return <span dir="ltr">{formatDecimalForDisplay(value, shaped[1]) ?? value}</span>;
  }
  if (TIMESTAMP_FIELDS.has(field)) {
    // Isolated, with its direction taken from its own content — never forced.
    // The Arabic rendering carries right-to-left marks, an Arabic comma and
    // "ص"/"م"; forcing it left-to-right draws the day after the year. The raw
    // ISO fallback has no right-to-left character, so it still reads LTR.
    return <bdi>{formatDateTime(value, { locale }) ?? value}</bdi>;
  }
  return <span {...fieldTextProps(field)}>{value}</span>;
}

// ---------------------------------------------------------------------------
// What changed between two submissions
// ---------------------------------------------------------------------------

/**
 * The revision comparison — the heart of the review screen.
 *
 * Every general-result field is listed, changed or not, so a reviewer can see
 * at a glance that a field they care about was left alone rather than having to
 * infer it from an omission. Equipment and attachments are matched by row
 * identity, so an entry is added or removed and never silently "edited".
 */
export function RevisionComparisonPanel({
  comparison,
  rowNames,
}: {
  readonly comparison: RevisionComparison | null;
  /** Names of D2216 determinations by row id, across the compared revisions. */
  readonly rowNames?: ReadonlyMap<string, string>;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();

  if (!comparison) {
    return (
      <Panel title={t('review.comparisonTitle')} description={t('review.comparisonHint')}>
        <p className="text-body text-default-600">{t('review.noComparison')}</p>
      </Panel>
    );
  }

  const beforeHeading =
    comparison.fromRevision === null
      ? t('review.noPreviousRevision')
      : t('review.revisionNumber', { number: comparison.fromRevision });
  const afterHeading = t('review.revisionNumber', { number: comparison.toRevision });

  return (
    <Panel title={t('review.comparisonTitle')} description={t('review.comparisonHint')}>
      <div className="flex flex-col gap-stack" data-testid="revision-comparison">
        {comparison.fromRevision === null ? (
          <Alert tone="info" title={t('review.firstSubmissionTitle')} description={t('review.firstSubmissionBody')} />
        ) : comparison.hasChanges ? null : (
          <Alert tone="info" title={t('review.noChangesTitle')} description={t('review.noChangesBody')} />
        )}

        <ul className="flex flex-col gap-stack">
          {comparison.fields.map((change) => (
            <li
              key={change.field}
              className="min-w-0 border-s-2 border-default-200 ps-3"
              data-testid={`comparison-field-${change.field}`}
            >
              <div className="flex flex-wrap items-center gap-inline">
                <span className="text-body font-medium text-default-900">
                  {changeLabel(change.field, t, rowNames)}
                </span>
                <StatusBadge
                  label={t(`revisionChange.${change.kind}`)}
                  tone={toneForChangeKind(change.kind)}
                />
              </div>
              <div className="mt-1 grid grid-cols-1 gap-stack sm:grid-cols-2">
                <ReadOnlyField label={beforeHeading}>
                  <FieldValue
                    field={change.field}
                    value={change.before}
                    absentLabel={t('common.notRecorded')}
                  />
                </ReadOnlyField>
                <ReadOnlyField label={afterHeading}>
                  <FieldValue
                    field={change.field}
                    value={change.after}
                    absentLabel={
                      change.kind === 'removed'
                        ? t('review.nowRemoved')
                        : t('common.notRecorded')
                    }
                  />
                </ReadOnlyField>
              </div>
            </li>
          ))}
        </ul>

        <p className="text-caption text-default-600">{t('review.emptyIsNotZero')}</p>

        <div className="border-t border-default-100 pt-stack">
          <p className="text-body font-medium text-default-900">
            {t('review.equipmentChangesTitle')}
          </p>
          {comparison.equipment.length === 0 ? (
            <p className="text-caption text-default-600">{t('review.noEquipmentChanges')}</p>
          ) : (
            <ul className="mt-1 flex flex-col gap-1">
              {comparison.equipment.map((change) => (
                <li key={`${change.kind}-${change.entry.usageId}`} className="flex flex-wrap items-center gap-inline">
                  <StatusBadge
                    label={t(`revisionChange.${change.kind}`)}
                    tone={toneForChangeKind(change.kind)}
                  />
                  <span className="text-body text-default-800">
                    <span dir="ltr">{change.entry.equipmentCode}</span>{' '}
                    {recordedEquipmentName(change.entry, locale)}
                  </span>
                  <span className="text-caption text-default-600">
                    {t('attempt.usedOn', { date: formatCalendarDate(change.entry.usedOn, locale) })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-default-100 pt-stack">
          <p className="text-body font-medium text-default-900">
            {t('review.attachmentChangesTitle')}
          </p>
          {comparison.attachments.length === 0 ? (
            <p className="text-caption text-default-600">{t('review.noAttachmentChanges')}</p>
          ) : (
            <ul className="mt-1 flex flex-col gap-1">
              {comparison.attachments.map((change) => (
                <li key={`${change.kind}-${change.entry.attachmentId}`} className="flex flex-wrap items-center gap-inline">
                  <StatusBadge
                    label={t(`revisionChange.${change.kind}`)}
                    tone={toneForChangeKind(change.kind)}
                  />
                  <span className="truncate text-body text-default-800" dir="auto">
                    {change.entry.filename}
                  </span>
                  <span className="text-caption text-default-600">
                    {t(`attachmentPurpose.${change.entry.purpose}`)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-caption text-default-600">{t('review.attachmentsNotRead')}</p>
        </div>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// One frozen submission, in full
// ---------------------------------------------------------------------------

function EquipmentLine({ entry }: { readonly entry: RevisionEquipmentEntry }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  return (
    <li className="min-w-0">
      <span className="text-body text-default-800">
        {entry.role ? (
          <span className="me-2 text-caption font-medium text-default-700">
            {t(`equipmentRole.${entry.role}`, { defaultValue: entry.role })}
          </span>
        ) : null}
        <span dir="ltr">{entry.equipmentCode}</span> {recordedEquipmentName(entry, locale)}
      </span>
      <span className="ms-2 text-caption text-default-600">
        {t('attempt.usedOn', { date: formatCalendarDate(entry.usedOn, locale) })}
      </span>
      {entry.overrideReason ? (
        <p className="text-caption font-medium text-danger-700">
          {t('review.usedByException', { note: entry.overrideReason })}
        </p>
      ) : null}
    </li>
  );
}

function AttachmentLine({ entry }: { readonly entry: RevisionAttachmentEntry }) {
  const { t } = useTranslation();
  return (
    <li className="min-w-0">
      <span className="truncate text-body text-default-800" dir="auto">
        {entry.filename}
      </span>
      <span className="ms-2 text-caption text-default-600">
        {t(`attachmentPurpose.${entry.purpose}`)}
      </span>
      {entry.note ? <p className="text-caption text-default-600">{entry.note}</p> : null}
    </li>
  );
}

/**
 * The complete content of one revision.
 *
 * Every field is shown, including the ones nobody filled in — stated as not
 * recorded rather than left blank, because a blank field and an absent one look
 * identical and mean different things (`CLAUDE.md` section 8).
 */
export function RevisionContent({ revision }: { readonly revision: ResultRevisionView }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-stack" data-testid={`revision-content-${revision.revisionNumber}`}>
      {revision.structured ? (
        isD2216Document(revision.structured) ? (
          <D2216Record document={revision.structured} />
        ) : isT96Document(revision.structured) ? (
          <LosAngelesAbrasionRecord document={revision.structured} />
        ) : (
          <SpecificGravityRecord document={revision.structured as D2726Document | D2041Document | T85Document} />
        )
      ) : null}
      <SubSection title={revision.structured ? t('bench.workDates') : t('attempt.generalResultTitle')}>
        <div className="grid grid-cols-1 gap-stack sm:grid-cols-2">
          {(revision.structured
            ? (['executionStartedAt', 'executionCompletedAt'] as const)
            : (['summaryAr', 'summaryEn', 'generalNotes', 'executionStartedAt', 'executionCompletedAt'] as const)
          ).map((field) => (
            <ReadOnlyField key={field} label={t(`resultField.${field}`)}>
              <FieldValue field={field} value={revision.content[field]} absentLabel={t('common.notRecorded')} />
            </ReadOnlyField>
          ))}
        </div>
      </SubSection>

      <SubSection title={t('attempt.equipmentTitle')}>
        {revision.equipment.length === 0 ? (
          <p className="text-body text-default-600">{t('attempt.noEquipment')}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {revision.equipment.map((entry) => (
              <EquipmentLine key={entry.usageId} entry={entry} />
            ))}
          </ul>
        )}
      </SubSection>

      <SubSection title={t('attempt.attachmentsTitle')}>
        {revision.attachments.length === 0 ? (
          <p className="text-body text-default-600">{t('attempt.noAttachments')}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {revision.attachments.map((entry) => (
              <AttachmentLine key={entry.attachmentId} entry={entry} />
            ))}
          </ul>
        )}
      </SubSection>
    </div>
  );
}

/** The submission a reviewer or approver is actually acting on. */
export function SubmittedRecordPanel({
  revision,
}: {
  readonly revision: ResultRevisionView | null;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();

  return (
    <Panel title={t('review.submittedRecordTitle')} description={t('review.submittedRecordHint')}>
      {revision === null ? (
        <p className="text-body text-default-600">{t('review.nothingSubmitted')}</p>
      ) : (
        <div className="flex flex-col gap-stack">
          <p className="text-caption text-default-600">
            {t('review.revisionNumber', { number: revision.revisionNumber })}
            {' · '}
            {t('review.submittedByOn', {
              name: revision.submittedBy.displayName ?? '',
              date: formatDateTime(revision.submittedAt, { locale }) ?? revision.submittedAt,
            })}
          </p>
          <RevisionContent revision={revision} />
        </div>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// The lock
// ---------------------------------------------------------------------------

/**
 * A lock is never deleted, only released — so both states are reported, and a
 * released lock still says who released it and against which correction.
 */
export function LockNotice({ lock }: { readonly lock: ResultLockView | null }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  if (!lock) return null;

  return lock.isActive ? (
    <Alert
      tone="info"
      title={t('review.lockedTitle')}
      description={t('review.lockedBody', {
        name: lock.lockedBy.displayName ?? '',
        date: formatDateTime(lock.lockedAt, { locale }) ?? lock.lockedAt,
      })}
    />
  ) : (
    <Alert
      tone="warning"
      title={t('review.lockReleasedTitle')}
      description={t('review.lockReleasedBody', {
        name: lock.releasedBy?.displayName ?? '',
        date: lock.releasedAt
          ? formatDateTime(lock.releasedAt, { locale }) ?? lock.releasedAt
          : '',
      })}
    />
  );
}

// ---------------------------------------------------------------------------
// The whole recorded history
// ---------------------------------------------------------------------------

interface TimelineEntry {
  readonly key: string;
  readonly at: string;
  readonly title: string;
  readonly who: string;
  readonly body: ReactNode;
}

/**
 * Every recorded act on this attempt, oldest first.
 *
 * APPEND-ONLY AND NEVER SUMMARISED. Earlier content stays in full and a return
 * reason is reproduced word for word: a laboratory that cannot see why work came
 * back cannot account for what happened to it.
 */
export function ResultHistoryPanel({ history }: { readonly history: ResultHistoryView }) {
  const { t } = useTranslation();
  const locale = currentLocale();

  const entries: TimelineEntry[] = [];

  for (const revision of history.revisions) {
    entries.push({
      key: `revision-${revision.id}`,
      at: revision.submittedAt,
      title: t('review.historyRevision', { number: revision.revisionNumber }),
      who: revision.submittedBy.displayName ?? '',
      body: <RevisionContent revision={revision} />,
    });
  }

  for (const action of history.reviewActions) {
    entries.push({
      key: `review-${action.id}`,
      at: action.actedAt,
      // Past tense: the history says what WAS decided, not the button label.
      title: t('review.historyReview', {
        decision: t(`reviewDecisionRecorded.${action.decision}`),
        number: action.revisionNumber,
      }),
      who: action.actedBy.displayName ?? '',
      body: <ReasonAndNote reason={action.reason} note={action.note} />,
    });
  }

  for (const action of history.approvalActions) {
    entries.push({
      key: `approval-${action.id}`,
      at: action.actedAt,
      title: t('review.historyApproval', {
        decision: t(`approvalDecisionRecorded.${action.decision}`),
        number: action.revisionNumber,
      }),
      who: action.actedBy.displayName ?? '',
      body: <ReasonAndNote reason={action.reason} note={action.note} />,
    });
  }

  for (const correction of history.corrections) {
    entries.push({
      key: `correction-open-${correction.id}`,
      at: correction.openedAt,
      title: t('corrections.historyOpened'),
      who: correction.openedBy.displayName ?? '',
      body: <ReasonAndNote reason={correction.reason} note={correction.summary} />,
    });
    if (correction.resolvedAt) {
      entries.push({
        key: `correction-resolved-${correction.id}`,
        at: correction.resolvedAt,
        title: t('corrections.historyResolved'),
        who: correction.resolvedBy?.displayName ?? '',
        body: null,
      });
    }
  }

  if (history.lock) {
    entries.push({
      key: `lock-${history.lock.id}`,
      at: history.lock.lockedAt,
      title: t('review.historyLocked', { number: history.lock.revisionNumber }),
      who: history.lock.lockedBy.displayName ?? '',
      body: null,
    });
    if (history.lock.releasedAt) {
      entries.push({
        key: `lock-released-${history.lock.id}`,
        at: history.lock.releasedAt,
        title: t('review.historyLockReleased'),
        who: history.lock.releasedBy?.displayName ?? '',
        body: null,
      });
    }
  }

  entries.sort((left, right) => left.at.localeCompare(right.at));

  return (
    <Panel title={t('review.historyTitle')} description={t('review.historyHint')}>
      {entries.length === 0 ? (
        <p className="text-body text-default-600">{t('review.historyEmpty')}</p>
      ) : (
        <ol className="flex flex-col gap-stack" data-testid="result-history">
          {entries.map((entry) => (
            <li key={entry.key} className="min-w-0 border-s-2 border-default-200 ps-3">
              <p className="text-body font-medium text-default-900">{entry.title}</p>
              {/* Name and date are isolated from each other: a Latin name
                  beside an Arabic-formatted date would otherwise pull the
                  date's digits into its own run and split the date apart. */}
              <p className="text-caption text-default-600">
                <bdi>{entry.who || t('common.notRecorded')}</bdi>
                {' · '}
                <bdi>{formatDateTime(entry.at, { locale }) ?? entry.at}</bdi>
              </p>
              {entry.body}
            </li>
          ))}
        </ol>
      )}
      <p className="mt-stack text-caption text-default-600">{t('review.historyAppendOnly')}</p>
    </Panel>
  );
}

function ReasonAndNote({
  reason,
  note,
}: {
  readonly reason: string | null;
  readonly note: string | null;
}) {
  const { t } = useTranslation();
  return (
    <>
      {reason ? (
        <p className="text-body text-default-800" dir="auto">
          {t('review.reasonGiven', { reason })}
        </p>
      ) : null}
      {note ? (
        <p className="text-caption text-default-600" dir="auto">
          {t('review.noteGiven', { note })}
        </p>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Corrections
// ---------------------------------------------------------------------------

/** Recorded correction procedures on this result. No withdrawal exists. */
export function CorrectionsPanel({
  corrections,
}: {
  readonly corrections: ResultHistoryView['corrections'];
}) {
  const { t } = useTranslation();
  const locale = currentLocale();

  return (
    <Panel title={t('corrections.title')} description={t('corrections.hint')}>
      {corrections.length === 0 ? (
        <p className="text-body text-default-600">{t('corrections.none')}</p>
      ) : (
        <ul className="flex flex-col gap-stack">
          {corrections.map((correction) => (
            <li key={correction.id} className="min-w-0 border-s-2 border-default-200 ps-3">
              <div className="flex flex-wrap items-center gap-inline">
                <StatusBadge
                  label={t(`correctionStatus.${correction.status}`)}
                  tone={correction.status === 'open' ? 'warning' : 'neutral'}
                />
                <span className="text-caption text-default-600">
                  {t('corrections.openedBy', {
                    name: correction.openedBy.displayName ?? '',
                    date: formatDateTime(correction.openedAt, { locale }) ?? correction.openedAt,
                  })}
                </span>
              </div>
              <p className="text-body text-default-800" dir="auto">
                {t('review.reasonGiven', { reason: correction.reason })}
              </p>
              {correction.summary ? (
                <p className="text-caption text-default-600" dir="auto">
                  {correction.summary}
                </p>
              ) : null}
              {correction.resolvedAt ? (
                <p className="text-caption text-default-600">
                  {t('corrections.resolvedBy', {
                    name: correction.resolvedBy?.displayName ?? '',
                    date:
                      formatDateTime(correction.resolvedAt, { locale }) ?? correction.resolvedAt,
                  })}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-stack text-caption text-default-600">{t('corrections.notWithdrawable')}</p>
    </Panel>
  );
}

/** The newest frozen submission, or null before anything was submitted. */
/**
 * "Determination 2 (can T-4)" for every D2216 row id in any revision, named
 * by its position in the LATEST revision that holds it.
 */
function determinationNames(history: ResultHistoryView): ReadonlyMap<string, string> {
  const names = new Map<string, string>();
  const ordered = [...history.revisions].sort((a, b) => a.revisionNumber - b.revisionNumber);
  for (const revision of ordered) {
    const document = revision.structured;
    if (!document) continue;
    if (isD2216Document(document)) {
      document.determinations.forEach((row, index) => {
        const number = String(index + 1);
        names.set(row.id, row.containerNumber ? `#${number} (${row.containerNumber})` : `#${number}`);
      });
    } else if (isT85Document(document)) {
      document.determinations.forEach((row, index) => names.set(row.id, `#${String(index + 1)}`));
    } else if (!isT96Document(document)) {
      // T96 has one grading table, named by its fractions rather than rows.
      const rows = isD2726Document(document) ? document.specimens : (document as D2041Document).determinations;
      rows.forEach((row, index) => {
        const number = String(index + 1);
        names.set(row.id, row.specimenNumber ? `#${number} (${row.specimenNumber})` : `#${number}`);
      });
    }
  }
  return names;
}

export function latestRevision(history: ResultHistoryView): ResultRevisionView | null {
  return history.revisions.reduce<ResultRevisionView | null>(
    (best, candidate) =>
      best === null || candidate.revisionNumber > best.revisionNumber ? candidate : best,
    null,
  );
}

/**
 * Everything a reviewer or an approver reads, in one order on both screens.
 *
 * The order is deliberate: what was submitted, then what changed since the last
 * submission, then the corrections on record, then the whole history. A person
 * deciding on a corrected result should meet the comparison before the archive.
 */
export function ResultRecordPanels({ history }: { readonly history: ResultHistoryView }) {
  return (
    <>
      <SubmittedRecordPanel revision={latestRevision(history)} />
      <RevisionComparisonPanel comparison={history.comparison} rowNames={determinationNames(history)} />
      <CorrectionsPanel corrections={history.corrections} />
      <ResultHistoryPanel history={history} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Identity of the work under review
// ---------------------------------------------------------------------------

/**
 * Which sample, which test, which attempt.
 *
 * The sample number links to the sample register so a reviewer can check what
 * arrived without losing this page.
 */
export function ResultIdentity({
  sampleId,
  sampleNumber,
  pieceCode,
  testLabel,
  attemptNumber,
  performedBy,
  testCode,
  status,
}: {
  readonly sampleId: string;
  readonly sampleNumber: string;
  readonly pieceCode: string | null;
  readonly testLabel: string;
  readonly attemptNumber: number;
  readonly performedBy: string;
  /** The catalogue code, a left-to-right technical value; omitted when none. */
  readonly testCode?: string | null;
  /** The state badge, drawn beside the identifiers. */
  readonly status?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="min-w-0 grow basis-64">
      <div className="flex flex-wrap items-center gap-x-inline gap-y-1">
        {testCode ? (
          <>
            <span className="measure text-title font-bold text-default-900" dir="ltr">
              {testCode}
            </span>
            <span className="text-title text-default-600" aria-hidden="true">
              ·
            </span>
          </>
        ) : null}
        <p className="measure text-title font-bold text-default-900" dir="ltr">
          <Link to={`/samples/${sampleId}`} className="hover:underline">
            {sampleNumber}
            {pieceCode ? ` / ${pieceCode}` : ''}
          </Link>
        </p>
        {status}
      </div>
      <p className="mt-0.5 text-body text-default-700">
        <bdi>{testLabel}</bdi>
        {' · '}
        <bdi>{t('attempt.performedBy', { name: performedBy })}</bdi>
        {' · '}
        <bdi>{t('attempt.numbered', { number: attemptNumber })}</bdi>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// What the record's own values say, gathered for the side column
// ---------------------------------------------------------------------------

/** The template a revision was submitted on, by its document. */
function templateOf(revision: ResultRevisionView): string | null {
  const document = revision.structured;
  if (!document) return null;
  if (isD2216Document(document)) return RESULT_TEMPLATE.ASTM_D2216_V1;
  if (isT96Document(document)) return RESULT_TEMPLATE.AASHTO_T96_V1;
  return revision.resultTemplate ?? (document as { templateKey?: string }).templateKey ?? null;
}

/** Every row of the frozen document carries the server's values. */
function everyRowCalculated(revision: ResultRevisionView): boolean {
  const document = revision.structured;
  if (!document) return false;
  if (isD2216Document(document)) {
    return document.determinations.length > 0 && document.determinations.every((row) => row.calculation !== null);
  }
  if (isT96Document(document)) return document.calculation !== null;
  const config = specificGravityFormFor(templateOf(revision));
  if (!config) return false;
  const rows = ((document as unknown as Record<string, unknown>)[config.rowsField] ?? []) as readonly { calculation?: unknown }[];
  return rows.length > 0 && rows.every((row) => row.calculation != null);
}

/**
 * The values a decision turns on, exactly as the server stored them: each
 * D2216 determination's water content, a specific gravity template's
 * averages, T96's masses and loss. Nothing is derived here.
 */
function keyResults(
  revision: ResultRevisionView,
  t: (key: string, options?: Record<string, unknown>) => string,
): { readonly key: string; readonly label: string; readonly value: ReactNode }[] {
  const document = revision.structured;
  if (!document) return [];
  const shown = (value: string | null | undefined, places: number, emphasis = true) => (
    <Reading
      value={formatDecimalForDisplay(value ?? null, places)}
      provenance="calculated"
      provenanceLabel={t('d2216.calculatedByServer')}
      absentLabel={t('d2216.notCalculated')}
      emphasis={emphasis}
    />
  );
  if (isD2216Document(document)) {
    return document.determinations.map((row, index) => ({
      key: row.id,
      label: `${t('d2216.determinationNumbered', { number: index + 1 })} · ${t('bench.waterContentShort')}`,
      value: shown(row.calculation?.waterContentPercent, MOISTURE_DISPLAY_PLACES.waterContent),
    }));
  }
  if (isT96Document(document)) {
    const mass = COARSE_AGGREGATE_DISPLAY_PLACES.mass;
    return [
      { key: 'total', label: t('t96.calculated.totalInitialMass'), value: shown(document.calculation?.totalInitialMass, mass, false) },
      { key: 'passing', label: t('t96.calculated.passingMassNo12'), value: shown(document.calculation?.passingMassNo12, mass, false) },
      {
        key: 'loss',
        label: t('t96.calculated.lossPercent'),
        value: shown(document.calculation?.lossPercent, COARSE_AGGREGATE_DISPLAY_PLACES.abrasionLoss),
      },
    ];
  }
  const config = specificGravityFormFor(templateOf(revision));
  if (!config) return [];
  const summary = config.storedSummary(document);
  return config.averages.map((average) => ({
    key: average.key,
    label: t(average.label),
    value: shown(summary[average.key], average.places),
  }));
}

/** The calculated results a decision turns on, on the calculated tint. Absent on the general path. */
export function KeyResultsPanel({ revision }: { readonly revision: ResultRevisionView | null }) {
  const { t } = useTranslation();
  if (!revision?.structured) return null;
  const values = keyResults(revision, t);
  if (values.length === 0) return null;
  return (
    <Panel title={t('bench.keyResults')} description={t('bench.keyResultsHint')}>
      <ResultGrid testId="key-results">
        {values.map((value) => (
          <ResultBlock key={value.key} label={value.label}>
            {value.value}
          </ResultBlock>
        ))}
      </ResultGrid>
    </Panel>
  );
}

/**
 * The verification checklist: facts read from the record as submitted. It
 * decides nothing — the reviewer's technical judgement is the decision, and
 * every act is re-checked by the server.
 */
export function VerificationChecklistPanel({ history }: { readonly history: ResultHistoryView }) {
  const { t } = useTranslation();
  const latest = latestRevision(history);
  if (!latest) {
    return (
      <Panel title={t('bench.verifyTitle')}>
        <p className="text-body text-default-600">{t('review.nothingSubmitted')}</p>
      </Panel>
    );
  }
  const roles = templateEquipmentRoles(templateOf(latest));
  const items: CheckItem[] = [
    ...(latest.structured ? [{ key: 'rows', ok: everyRowCalculated(latest), label: t('bench.verify.allCalculated') }] : []),
    roles.length > 0
      ? { key: 'roles', ok: roles.every((role) => latest.equipment.some((entry) => entry.role === role)), label: t('bench.check.instruments') }
      : { key: 'equipment', ok: latest.equipment.length > 0, label: t('bench.verify.equipmentRecorded') },
    { key: 'exception', ok: latest.equipment.every((entry) => !entry.overrideReason), label: t('bench.verify.noException') },
    {
      key: 'dates',
      ok: Boolean(latest.content.executionStartedAt && latest.content.executionCompletedAt),
      label: t('bench.check.dates'),
    },
    { key: 'own', ok: !history.callerIsPerformer, label: t('bench.verify.notOwnWork') },
  ];
  return (
    <Panel title={t('bench.verifyTitle')} description={t('bench.verifyHint')}>
      <CheckList items={items} />
    </Panel>
  );
}
